import { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Music2, Volume2, Monitor, Video, Palette, Zap, ArrowRight, UtensilsCrossed, Sofa, ShieldCheck, Droplets } from 'lucide-react';

const services = [
  {
    id: 'entertain',
    icon: Music2,
    title: 'Entertainment Bookings',
    description:
      'From electrifying bands and charismatic MCs to talented solo artists, professional promo models, and captivating dancers — our entertainment roster is curated to deliver unforgettable moments. We match the right talent to your event\'s unique energy.',
    features: ['Live Bands & Musicians', 'Professional MCs & Hosts', 'Solo Artists', 'Promotional Models', 'Dance Performers', 'Celebrity Appearances'],
    image: '/brand/service-entertainment.jpg',
  },
  {
    id: 'technical',
    icon: Volume2,
    title: 'Technical Production',
    description:
      'Crystal-clear sound, stunning lighting design, and rock-solid staging — our technical team ensures every element of your event runs flawlessly. We handle the complexity so you can focus on the experience.',
    features: ['Professional Sound Systems', 'Intelligent Lighting Design', 'Stage Construction', 'Power Distribution', 'Rigging & Trussing', 'Technical Direction'],
    image: '/brand/service-technical.jpg',
  },
  {
    id: 'led',
    icon: Monitor,
    title: 'LED Screens',
    description:
      'Transform your venue with high-resolution LED displays. Perfect for live video feeds, branded content, dynamic visuals, and immersive backdrops that captivate your audience from every angle.',
    features: ['High-Resolution LED Walls', 'Mobile LED Screens', 'Video Mapping', 'Live Feed Display', 'Branded Content Display', 'Interactive Visuals'],
    image: '/brand/service-led.jpg',
  },
  {
    id: 'streaming',
    icon: Video,
    title: 'Live Streaming & Recording',
    description:
      'Extend your event\'s reach with professional multi-camera live streaming. We also provide full event recording services so your moments are preserved in broadcast quality.',
    features: ['Multi-Camera Setup', 'Live Streaming', 'Event Recording', 'Post-Production Editing', 'Virtual Event Production', 'Hybrid Event Solutions'],
    image: '/brand/service-streaming.jpg',
  },
  {
    id: 'content',
    icon: Palette,
    title: 'Custom Content Creation',
    description:
      'From professional voice-overs and promotional videos to motion graphics and print design — we create custom content that amplifies your event\'s brand and message.',
    features: ['Promotional Videos', 'Motion Graphics', 'Voice-Over Production', 'Event Branding', 'Print Design', 'Social Media Content'],
    image: '/brand/service-content.jpg',
  },
  {
    id: 'effects',
    icon: Zap,
    title: 'Special Effects',
    description:
      'Gobo light projections, laser shows, atmospheric smoke, intelligent moving lights, and dramatic low fog — our special effects transform ordinary venues into extraordinary experiences.',
    features: ['Gobo Projections', 'Laser Shows', 'Smoke & Haze Machines', 'Low Fog Effects', 'Pyrotechnics', 'Confetti & Streamers'],
    image: '/brand/service-effects.jpg',
  },
  {
    id: 'catering',
    icon: UtensilsCrossed,
    title: 'Catering and Bar',
    description:
      'From curated menus to fully staffed bar services, we manage catering coordination end-to-end. We work with trusted suppliers to deliver food and beverage experiences that match the scale, tone, and theme of your event.',
    features: ['Menu Planning & Design', 'Staffed Bar Service', 'Beverage Coordination', 'Dietary Accommodation', 'Supplier Management', 'On-Site Coordination'],
    image: '/brand/service-catering.webp',
  },
  {
    id: 'decor',
    icon: Sofa,
    title: 'Décor and Furniture',
    description:
      'The right environment sets the tone before a word is spoken. We source, design, and coordinate event décor and furniture to ensure every space feels considered and aligned with your brand or event theme.',
    features: ['Concept and Theme Design', 'Furniture Sourcing & Hire', 'Floral and Styling', 'Branded Décor Elements', 'Linen and Tableware', 'Space Dressing and Setup'],
    image: '/brand/service-decor.webp',
  },
  {
    id: 'hns',
    icon: ShieldCheck,
    title: 'Health & Safety / JOC',
    description:
      'A well-run event is a safe event. We manage health and safety planning and Joint Operations Centre (JOC) coordination to ensure your event is compliant, controlled, and prepared for every contingency.',
    features: ['Health & Safety Planning', 'JOC Setup & Management', 'Crowd Management', 'Emergency Protocols', 'Compliance Documentation', 'On-Site Safety Officers'],
    image: '/brand/service-hns.webp',
  },
  {
    id: 'watercurtain',
    icon: Droplets,
    title: 'Water Curtain',
    description:
      'Transform any backdrop with a suspended sheet of falling water — an elegant and dramatic visual element that adds movement, depth, and ambience to your event environment.',
    features: ['Custom Installations', 'Indoor & Outdoor Setups', 'Integrated Lighting Effects', 'Branded Water Displays', 'Multiple Width Options', 'Full Setup & Strike'],
    image: null,
  },
];

const ServiceBlock = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isEven = index % 2 === 0;

  return (
    <section
      id={service.id}
      ref={ref}
      className={`py-16 md:py-24 ${isEven ? 'bg-white' : 'bg-[#F5F8FA]'}`}
      data-testid={`service-detail-${service.id}`}
    >
      <div className={`max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, x: isEven ? -40 : 40 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
          className={`relative ${!isEven ? 'lg:order-2' : ''}`}
        >
          <div className="relative aspect-[4/3] overflow-hidden rounded-xl">
            {service.image ? (
              <>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 bg-[#F0F4F7] border-2 border-dashed border-[#47c3d6]">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#47c3d6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>
                <span className="text-[#17798A] text-sm font-semibold tracking-widest uppercase">Photo Coming Soon</span>
              </div>
            )}
          </div>
          {/* Accent */}
          <div className={`absolute -bottom-4 ${isEven ? '-right-4' : '-left-4'} w-full h-full border border-[#47c3d6]/30 -z-10 rounded-xl`} />
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, x: isEven ? 40 : -40 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className={!isEven ? 'lg:order-1' : ''}
        >
          <div className="w-14 h-14 bg-[#47c3d6]/10 border border-[#47c3d6]/30 rounded-xl flex items-center justify-center mb-6">
            <service.icon size={26} className="text-[#47c3d6]" />
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#111111] mb-6">
            {service.title}
          </h2>

          <p className="text-[#666666] text-base sm:text-lg leading-relaxed mb-8">
            {service.description}
          </p>

          {/* Features */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {service.features.map((feature) => (
              <div
                key={feature}
                className="flex items-center gap-2 text-sm text-[#555555]"
              >
                <div className="w-1.5 h-1.5 bg-[#47debd] rounded-full" />
                {feature}
              </div>
            ))}
          </div>

          {/* CTA */}
          <Link
            to="/#book"
            className="inline-flex items-center gap-3 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white bg-[#17798A] hover:bg-[#12626F] transition-all duration-300 group rounded-lg"
            data-testid={`book-${service.id}`}
          >
            Book This Service
            <ArrowRight
              size={18}
              className="group-hover:translate-x-1 transition-transform"
            />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export const ServicesPage = () => {
  const location = useLocation();
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });

  useEffect(() => {
    // Scroll to hash on page load
    const hash = location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <main id="main-content" tabIndex={-1} data-testid="services-page">
      <SEO
        title="Our Services — Full Event Production"
        description="Ten end-to-end event production services in Pretoria: entertainment, technical production, LED screens, live streaming, effects, catering and décor."
        path="/services"
        image="/brand/services-hero.jpg"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
        ]}
      />
      {/* Hero Banner */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24"
      >
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="/brand/services-hero.jpg"
            alt="New Creative Industries full-service event production in Pretoria"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-white" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-[#47debd] font-bold mb-4"
          >
            New Creative Industries — Full Service
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-none text-white"
          >
            Every Detail.
            <br />
            <span className="text-[#47c3d6]">One Team.</span>
          </motion.h1>
        </div>
      </section>

      {/* Services */}
      {services.map((service, index) => (
        <ServiceBlock key={service.id} service={service} index={index} />
      ))}
    </main>
  );
};

export default ServicesPage;
